import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

const wrapperBase =
  "min-h-screen w-full bg-ambient text-foreground animate-in fade-in duration-fast";

const containerBase = "mx-auto w-full max-w-[1400px] px-4 md:px-6 lg:px-8 py-6 lg:py-8";

function HeroSkeleton({ titleWidth = "w-64" }: { titleWidth?: string }) {
  return (
    <section className="relative overflow-hidden rounded-[1.9rem] glass-subtle shadow-glass border border-border/50 p-5">
      <div className="flex flex-col gap-3">
        <Skeleton className="h-3 w-24 rounded-full opacity-70" />
        <Skeleton className={cn("h-8 rounded-md", titleWidth)} />
        <Skeleton className="h-4 w-80 max-w-full rounded opacity-70" />
      </div>
    </section>
  );
}

function KpiTileSkeletonInline() {
  return (
    <div className="rounded-2xl glass-subtle shadow-glass px-4 py-3.5">
      <Skeleton className="h-10 w-10 rounded-xl mb-3" />
      <Skeleton className="h-8 w-20 mb-2" />
      <Skeleton className="h-3 w-28" />
    </div>
  );
}

function SectionCardSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="rounded-2xl bg-card border border-border/70 shadow-soft p-5 space-y-4">
      <div className="flex items-center justify-between">
        <Skeleton className="h-5 w-40 rounded" />
        <Skeleton className="h-7 w-20 rounded-full opacity-70" />
      </div>
      <div className="space-y-3">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="h-8 w-8 rounded-full opacity-70" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-3.5 w-3/5 rounded" />
              <Skeleton className="h-3 w-2/5 rounded opacity-70" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DashboardSkeleton() {
  return (
    <div className={wrapperBase} data-tint="brand" aria-busy="true" aria-live="polite">
      <span className="sr-only">Chargement du tableau de bord...</span>
      <div className={containerBase}>
        <div className="space-y-6">
          <HeroSkeleton titleWidth="w-72" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <KpiTileSkeletonInline key={i} />
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SectionCardSkeleton rows={4} />
            <SectionCardSkeleton rows={3} />
          </div>
          <SectionCardSkeleton rows={3} />
        </div>
      </div>
    </div>
  );
}

export function EmployeesSkeleton() {
  return (
    <div className={wrapperBase} data-tint="brand" aria-busy="true" aria-live="polite">
      <span className="sr-only">Chargement des employés...</span>
      <div className={containerBase}>
        <div className="space-y-6">
          <HeroSkeleton titleWidth="w-56" />
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <Skeleton className="h-10 flex-1 rounded-lg" />
            <Skeleton className="h-10 w-40 rounded-lg" />
          </div>
          <div className="rounded-2xl bg-card border border-border/70 shadow-soft divide-y divide-border/50">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 p-4">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-4 w-44 rounded" />
                  <Skeleton className="h-3 w-28 rounded opacity-70" />
                </div>
                <Skeleton className="hidden md:block h-6 w-20 rounded-full opacity-70" />
                <Skeleton className="h-8 w-8 rounded-md opacity-60" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function VacationsSkeleton() {
  return (
    <div className={wrapperBase} data-tint="brand" aria-busy="true" aria-live="polite">
      <span className="sr-only">Chargement des congés...</span>
      <div className={containerBase}>
        <div className="space-y-6">
          <HeroSkeleton titleWidth="w-60" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <KpiTileSkeletonInline key={i} />
            ))}
          </div>
          <div className="rounded-2xl bg-card border border-border/70 shadow-soft p-5 space-y-3">
            <div className="flex items-center justify-between">
              <Skeleton className="h-5 w-40 rounded" />
              <Skeleton className="h-9 w-32 rounded-lg opacity-70" />
            </div>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-muted/30">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-4 w-48 rounded" />
                  <Skeleton className="h-3 w-32 rounded opacity-70" />
                </div>
                <Skeleton className="h-7 w-24 rounded-full opacity-70" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function SettingsSkeleton() {
  return (
    <div className={wrapperBase} data-tint="brand" aria-busy="true" aria-live="polite">
      <span className="sr-only">Chargement des paramètres...</span>
      <div className={containerBase}>
        <div className="space-y-6">
          <HeroSkeleton titleWidth="w-48" />
          <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
            <aside className="rounded-2xl bg-card border border-border/70 shadow-soft p-3 space-y-2 hidden lg:block">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg">
                  <Skeleton className="h-5 w-5 rounded opacity-70" />
                  <Skeleton className="h-4 flex-1 rounded opacity-70" />
                </div>
              ))}
            </aside>
            <div className="rounded-2xl bg-card border border-border/70 shadow-soft p-5 space-y-5">
              <Skeleton className="h-6 w-56 rounded" />
              <div className="space-y-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="h-3.5 w-32 rounded opacity-70" />
                    <Skeleton className="h-10 w-full rounded-lg" />
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Skeleton className="h-10 w-24 rounded-lg opacity-70" />
                <Skeleton className="h-10 w-32 rounded-lg" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function SuiviRhSkeleton() {
  return (
    <div className={wrapperBase} data-tint="brand" aria-busy="true" aria-live="polite">
      <span className="sr-only">Chargement du suivi planning et heures…</span>
      <div className={containerBase}>
        <div className="space-y-6">
          <HeroSkeleton titleWidth="w-64" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <KpiTileSkeletonInline key={i} />
            ))}
          </div>
          <div className="rounded-2xl bg-card border border-border/70 shadow-soft overflow-hidden">
            <div className="flex items-center justify-between gap-3 border-b border-border/60 p-4">
              <div className="space-y-2">
                <Skeleton className="h-5 w-52 rounded" />
                <Skeleton className="h-3 w-72 max-w-full rounded opacity-70" />
              </div>
              <Skeleton className="h-9 w-36 rounded-lg opacity-70" />
            </div>
            <div className="overflow-hidden">
              <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr] gap-0 border-b border-border/60 bg-muted/30 p-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-3 w-24 max-w-[80%] rounded opacity-70" />
                ))}
              </div>
              {Array.from({ length: 7 }).map((_, row) => (
                <div key={row} className="grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr] items-center gap-3 border-b border-border/40 p-3 last:border-b-0">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-9 w-9 rounded-full" />
                    <div className="space-y-1.5">
                      <Skeleton className="h-4 w-32 rounded" />
                      <Skeleton className="h-3 w-20 rounded opacity-70" />
                    </div>
                  </div>
                  {Array.from({ length: 4 }).map((_, col) => (
                    <Skeleton key={col} className="h-7 w-24 rounded-full opacity-80" />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
